import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { RoomAudioRenderer } from '@livekit/components-react';

import { IUser } from '../../lib/interfaces';
import CallInProgressParticipants from './CallInProgressParticipants';
import ControlBar from './components/ControlBar';
import { fadeInSx } from './designTokens';
import DeviceFailedBanner from './components/DeviceFailedBanner';
import EndCallDialog from './components/EndCallDialog';
import {
  findPatientParticipant,
  useCallParticipants,
} from './hooks/useCallParticipants';
import SidePanel from './components/SidePanel';

type Props = {
  patient: IUser;
  onLeave: () => void;
};

const CallInProgress = ({ patient, onLeave }: Props) => {
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const { self, participants } = useCallParticipants();
  const remote = findPatientParticipant(participants);
  const others = participants.filter((participant) => participant !== remote);

  const cancelEnd = useCallback(() => setConfirmingEnd(false), []);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          lg: 'minmax(0, 1fr) 320px',
        },
        gap: '20px',
        minHeight: 0,
        ...fadeInSx,
      }}
    >
      <RoomAudioRenderer />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          minHeight: 0,
        }}
      >
        <DeviceFailedBanner />

        <CallInProgressParticipants
          self={self}
          patient={patient}
          remote={remote}
          others={others}
        />

        <ControlBar onEnd={() => setConfirmingEnd(true)} />
      </Box>

      <SidePanel self={self} remote={remote} patient={patient} />

      <EndCallDialog
        open={confirmingEnd}
        patient={patient}
        onCancel={cancelEnd}
        onConfirm={onLeave}
      />
    </Box>
  );
};

export default CallInProgress;
